/**
 * authFetch.ts — Authenticated fetch helper for raw API calls.
 *
 * WHY THIS EXISTS:
 *   The global bearerFetchPatch intercepts window.fetch and tries to inject a
 *   Bearer token by calling getSession() from *inside* the fetch interceptor
 *   chain. In production (Cloud Run), this consistently returns null — likely
 *   because getSession() called from within a patched window.fetch hits a
 *   re-entrancy or timing constraint in the Supabase JS SDK.
 *
 *   Pipeline 1 (the generated API client / customFetch) works reliably because
 *   it calls the token getter BEFORE invoking window.fetch — avoiding the
 *   interceptor context entirely. This helper replicates that exact pattern for
 *   raw fetch call sites (use-opportunities, use-dashboard, use-events,
 *   use-settings, mission-control).
 *
 * USAGE:
 *   Replace:  await fetch('/api/opportunities', { credentials: 'include' })
 *   With:     await authFetch('/api/opportunities')
 *
 *   authFetch always sets credentials:'include' and injects the Bearer token
 *   if a Supabase session exists. Pass additional init overrides as the second
 *   argument (they are merged; headers are merged rather than replaced).
 */

import { supabase } from './supabaseClient';

export async function authFetch(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  // Get the current session directly — same pattern Pipeline 1 uses.
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? null;

  // Build merged headers so callers can still pass their own.
  const base =
    init.headers instanceof Headers
      ? init.headers
      : new Headers(init.headers as HeadersInit | undefined);

  if (token && !base.has('authorization')) {
    base.set('Authorization', `Bearer ${token}`);
  }

  return fetch(url, {
    credentials: 'include',
    ...init,
    headers: base,
  });
}
